/**
 * Level-Up Logic
 *
 * Pure functions for D&D 5e character leveling.
 * No side effects, no store imports — just character in, character out.
 */
import { getModifier } from './character-types';
import { XP_THRESHOLDS, getXpForNextLevel, CLASS_FEATURES, CLASS_HIT_DIE, isAsiLevel, getSpellSlots, SUBCLASS_LEVEL, meetsMulticlassPrereqs, CLASS_CASTER_TYPE, CLASS_SAVE_PROFICIENCIES, } from './class-progression';
// =============================================================================
// CORE FUNCTIONS
// =============================================================================
/** Check if character has enough XP to level up */
export function canLevelUp(character) {
    const totalLevel = character.classes.reduce((sum, c) => sum + c.level, 0);
    if (totalLevel >= 20)
        return false;
    const nextThreshold = XP_THRESHOLDS[totalLevel];
    return (character.xp ?? 0) >= nextThreshold;
}
/** Get XP progress info for display */
export function getXpProgress(character) {
    const totalLevel = character.classes.reduce((sum, c) => sum + c.level, 0);
    const currentXp = character.xp ?? 0;
    const nextThreshold = getXpForNextLevel(totalLevel);
    const prevThreshold = totalLevel > 0 ? XP_THRESHOLDS[totalLevel - 1] : 0;
    let percentage = 100;
    if (nextThreshold !== null) {
        const range = nextThreshold - prevThreshold;
        percentage = range > 0 ? Math.min(100, ((currentXp - prevThreshold) / range) * 100) : 100;
    }
    return { currentXp, nextThreshold, totalLevel, percentage };
}
/** Preview what will happen when leveling up a specific class */
export function getLevelUpPreview(character, classIndex) {
    const totalLevel = character.classes.reduce((sum, c) => sum + c.level, 0);
    const cls = character.classes[classIndex];
    if (!cls)
        throw new Error(`Invalid class index: ${classIndex}`);
    const newClassLevel = cls.level + 1;
    const newTotalLevel = totalLevel + 1;
    const newProficiencyBonus = Math.ceil(1 + newTotalLevel / 4);
    const hitDie = cls.hitDie || CLASS_HIT_DIE[cls.name] || 8;
    const conMod = getModifier(character.abilityScores.constitution);
    const hpAverage = Math.ceil(hitDie / 2) + 1;
    const newFeatures = (CLASS_FEATURES[cls.name] ?? [])
        .filter((f) => f.level === newClassLevel);
    const needsAsi = isAsiLevel(cls.name, newClassLevel);
    const needsSubclass = !cls.subclass && SUBCLASS_LEVEL[cls.name] === newClassLevel;
    const casterType = CLASS_CASTER_TYPE[cls.name] ?? 'none';
    // EK/AT override
    const isThirdCaster = (cls.name === 'Fighter' && cls.subclass === 'Eldritch Knight') ||
        (cls.name === 'Rogue' && cls.subclass === 'Arcane Trickster');
    const needsSpellSelection = casterType !== 'none' || isThirdCaster;
    const newSpellSlots = getSpellSlots(cls.name, newClassLevel, cls.subclass);
    // Multiclass options (classes the character doesn't already have)
    const existingClassNames = new Set(character.classes.map((c) => c.name));
    const allClassNames = [
        'Barbarian', 'Bard', 'Cleric', 'Druid', 'Fighter', 'Monk',
        'Paladin', 'Ranger', 'Rogue', 'Sorcerer', 'Warlock', 'Wizard', 'Artificer',
    ];
    const canMulticlass = allClassNames
        .filter((cn) => !existingClassNames.has(cn))
        .map((cn) => ({
        className: cn,
        meetsPrereqs: meetsMulticlassPrereqs(cn, character.abilityScores),
    }));
    return {
        currentTotalLevel: totalLevel,
        newTotalLevel,
        classBeingLeveled: cls.name,
        newClassLevel,
        needsHpChoice: true,
        needsAsi,
        needsSubclass,
        needsSpellSelection,
        newSpellSlots,
        newFeatures,
        newProficiencyBonus,
        hitDie,
        hpAverage,
        conMod,
        canMulticlass,
    };
}
/** Apply level-up choices and return a new character (immutable) */
export function applyLevelUp(character, choices) {
    const updated = { ...character };
    const classes = [...character.classes.map((c) => ({ ...c }))];
    // 1. Determine which class to level
    let classIdx = choices.classIndex;
    if (classIdx === -1 && choices.newClassName) {
        // Multiclass: add new class entry
        const hitDie = CLASS_HIT_DIE[choices.newClassName] || 8;
        const saves = CLASS_SAVE_PROFICIENCIES[choices.newClassName] || [];
        classes.push({
            name: choices.newClassName,
            level: 0, // Will be incremented below
            hitDie,
        });
        classIdx = classes.length - 1;
        // Multiclass doesn't grant save proficiencies from the new class
        // but does grant some skill/armor proficiencies (simplified: just note in features)
        updated.features = [
            ...(character.features || []),
            {
                name: `Multiclass: ${choices.newClassName}`,
                source: `${choices.newClassName} 1`,
                description: `Multiclassed into ${choices.newClassName}. Save proficiencies from this class: ${saves.join(', ')} (only from first class).`,
            },
        ];
    }
    // 2. Increment class level
    classes[classIdx].level += 1;
    updated.classes = classes;
    const cls = classes[classIdx];
    const newTotalLevel = classes.reduce((sum, c) => sum + c.level, 0);
    // 3. Proficiency bonus
    updated.proficiencyBonus = Math.ceil(1 + newTotalLevel / 4);
    // 4. HP gain
    const hitDie = cls.hitDie || CLASS_HIT_DIE[cls.name] || 8;
    const conMod = getModifier(character.abilityScores.constitution);
    let hpGain;
    if (choices.hpMethod === 'roll' && choices.hpRollResult != null) {
        hpGain = Math.max(1, choices.hpRollResult + conMod);
    }
    else {
        const avg = Math.ceil(hitDie / 2) + 1;
        hpGain = Math.max(1, avg + conMod);
    }
    updated.maxHp = character.maxHp + hpGain;
    updated.currentHp = character.currentHp + hpGain; // Gain HP immediately
    // Update hit dice string
    const diceBySize = new Map();
    for (const c of classes) {
        const die = c.hitDie || CLASS_HIT_DIE[c.name] || 8;
        diceBySize.set(die, (diceBySize.get(die) || 0) + c.level);
    }
    updated.hitDice = Array.from(diceBySize.entries())
        .sort((a, b) => b[0] - a[0]) // Largest die first
        .map(([die, count]) => `${count}d${die}`)
        .join('+');
    updated.hitDiceRemaining = (character.hitDiceRemaining ?? 0) + 1;
    // 5. ASI / Feat
    if (choices.asi) {
        if (choices.asi.type === 'asi') {
            const scores = { ...character.abilityScores };
            const a1 = choices.asi.ability1;
            const a2 = choices.asi.ability2;
            scores[a1] = Math.min(20, (scores[a1] || 10) + 1);
            scores[a2] = Math.min(20, (scores[a2] || 10) + 1);
            updated.abilityScores = scores;
            // Recalculate CON-dependent HP if CON was boosted
            if (a1 === 'constitution' || a2 === 'constitution') {
                const newConMod = getModifier(scores.constitution);
                const oldConMod = getModifier(character.abilityScores.constitution);
                const conDiff = newConMod - oldConMod;
                if (conDiff > 0) {
                    // Retroactive HP gain for all levels
                    updated.maxHp += conDiff * newTotalLevel;
                    updated.currentHp += conDiff * newTotalLevel;
                }
            }
        }
        else {
            // Feat — record it as a feature
            updated.features = [
                ...(updated.features || []),
                {
                    name: choices.asi.featName,
                    source: `${cls.name} ${cls.level} (Feat)`,
                    description: `Feat selected at ${cls.name} level ${cls.level}. Apply effects manually.`,
                },
            ];
        }
    }
    // 6. Subclass
    if (choices.subclass) {
        classes[classIdx].subclass = choices.subclass;
        updated.classes = classes;
    }
    // 7. Add new class features
    const newFeatures = (CLASS_FEATURES[cls.name] ?? [])
        .filter((f) => f.level === cls.level);
    if (newFeatures.length > 0) {
        updated.features = [
            ...(updated.features || []),
            ...newFeatures.map((f) => ({
                name: f.name,
                source: `${cls.name} ${cls.level}`,
                description: f.description,
            })),
        ];
    }
    // 8. Update spell slots
    const casterType = CLASS_CASTER_TYPE[cls.name] ?? 'none';
    const isThirdCaster = (cls.name === 'Fighter' && cls.subclass === 'Eldritch Knight') ||
        (cls.name === 'Rogue' && cls.subclass === 'Arcane Trickster');
    if (casterType !== 'none' || isThirdCaster) {
        const newSlots = getSpellSlots(cls.name, cls.level, cls.subclass);
        if (Object.keys(newSlots).length > 0) {
            updated.spellcasting = {
                ability: updated.spellcasting?.ability || getDefaultCastingAbility(cls.name),
                spellSlots: newSlots,
                spellSlotsUsed: updated.spellcasting?.spellSlotsUsed || {},
                knownSpells: updated.spellcasting?.knownSpells || [],
                preparedSpells: updated.spellcasting?.preparedSpells || [],
            };
        }
    }
    // 9. Add new spells
    if (choices.newSpells && choices.newSpells.length > 0 && updated.spellcasting) {
        updated.spellcasting = {
            ...updated.spellcasting,
            knownSpells: [...(updated.spellcasting.knownSpells || []), ...choices.newSpells],
        };
    }
    // 10. Spell swap
    if (choices.swapSpell && updated.spellcasting) {
        const known = [...(updated.spellcasting.knownSpells || [])];
        const idx = known.indexOf(choices.swapSpell.old);
        if (idx >= 0) {
            known[idx] = choices.swapSpell.new;
            updated.spellcasting = { ...updated.spellcasting, knownSpells: known };
        }
    }
    // 11. Clear pending level-up flag
    updated.pendingLevelUp = canLevelUp(updated); // Check if they can level again
    updated.updatedAt = new Date();
    return updated;
}
// =============================================================================
// HELPERS
// =============================================================================
function getDefaultCastingAbility(className) {
    switch (className) {
        case 'Wizard':
        case 'Artificer':
            return 'intelligence';
        case 'Cleric':
        case 'Druid':
        case 'Ranger':
        case 'Monk':
            return 'wisdom';
        case 'Bard':
        case 'Sorcerer':
        case 'Warlock':
        case 'Paladin':
            return 'charisma';
        default:
            return 'intelligence';
    }
}
//# sourceMappingURL=level-up.js.map